/**
 * Mothership — the player's mobile hive. Level 1 ("The Prospector") data
 * structure and the runtime state that hangs off it. Higher tiers reuse the
 * same struct; `applyTier` re-derives caps/limits from the level table.
 */
import { Vector2 } from "@/math/Vector2";
import { SHIP_DEFENSE } from "@/config/balance";
import {
  MOTHERSHIP_LEVELS,
  type MothershipTier,
  type MothershipCapabilities,
} from "./levels";
import type { MiningDrone } from "./MiningDrone";

export interface Mothership {
  tier: MothershipTier;
  name: string;
  pos: Vector2;
  vel: Vector2;
  heading: number; // radians, for sprite orientation
  moveTarget: Vector2 | null; // tap-to-move destination, or null when idle

  hp: number;
  maxHp: number;
  shield: number;
  maxShield: number;

  // capability-derived limits (kept denormalised for fast reads in the loop)
  caps: MothershipCapabilities;
  droneBays: number;
  hangarCapacity: number;
  technicianMaxTier: number;

  // live sub-systems
  drones: MiningDrone[];
  /** Current idle-repair technician investment (0..technicianMaxTier). */
  technicianTier: number;
}

/** Build a fresh Level-1 Prospector at `pos`. */
export function createMothership(pos = new Vector2(0, 0)): Mothership {
  const lvl = MOTHERSHIP_LEVELS[1];
  const ship: Mothership = {
    tier: 1,
    name: lvl.name,
    pos,
    vel: new Vector2(0, 0),
    heading: Math.PI / 2, // sprite's "front" (drill end) is drawn pointing down (+Y)
    moveTarget: null,
    hp: lvl.maxHp,
    maxHp: lvl.maxHp,
    shield: SHIP_DEFENSE.maxShield,
    maxShield: SHIP_DEFENSE.maxShield,
    caps: lvl.capabilities,
    droneBays: lvl.droneBays,
    hangarCapacity: lvl.hangarCapacity,
    technicianMaxTier: lvl.technicianMaxTier,
    // Level 1 starts with ZERO drones — the player must pilot the hull to
    // asteroids and mine manually until they can afford to build drones.
    drones: [],
    technicianTier: 0,
  };
  return ship;
}

/** Re-derive limits when the hull is upgraded to a new tier. */
export function applyTier(ship: Mothership, tier: MothershipTier): void {
  const lvl = MOTHERSHIP_LEVELS[tier];
  ship.tier = tier;
  ship.name = lvl.name;
  ship.maxHp = lvl.maxHp;
  ship.hp = Math.min(ship.hp, lvl.maxHp) || lvl.maxHp;
  ship.caps = lvl.capabilities;
  ship.droneBays = lvl.droneBays;
  ship.hangarCapacity = lvl.hangarCapacity;
  ship.technicianMaxTier = lvl.technicianMaxTier;
}

/** Can we build another mining drone right now? */
export function canBuildDrone(ship: Mothership): boolean {
  return ship.caps.miningDrones && ship.drones.length < ship.droneBays;
}
